<script setup>
/**
 * @description 通用的首页 Banner 业务组件,负责数据获取和业务逻辑
 * @param {number} displayPage - 页面显示位置,用于本地数据过滤
 * @param {string|number|null} categoryId - 分类聚合页所属一级分类 ID
 * @emits buy - 当点击立即购买按钮时触发,传递当前选中的 banner 数据
 * @emits empty - 当 banner 数据为空时触发
 */

// 1. Imports
import { websiteApi } from '@/api/website'
import { useMediaQuery } from '@/composables/useMediaQuery'
import { useUserStore } from '~/store/common/user'
import { BaseMessage } from '~/utils/baseMessage'
import { hasExposedInPage, isSelectorInViewport, markExposedInPage, safeTrack } from '~/utils/tracker'
import {
  extractStoreProductIdFromUrl,
  rememberProductEntrySource,
} from '~/utils/tracking/sourceContext'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { getImageTheme, getOssOriginUrl } from '@/utils'
import { DISPLAY_PAGE } from '@/utils/constant'
import { THEME_BREAKPOINTS } from '~/utils/theme'
import { createPublicContentSsrRequestConfig } from '~/utils/website/ssrRequestPolicy'

/** 使用 OSS 裁剪 + 浏览器像素均值的轮播（PC：居中 1280 左 640；移动：同条带居中 + 上半采样），与首页/方案/商城 Banner 一致 */
const BAND_IMAGE_THEME_DISPLAY_PAGES = new Set([
  DISPLAY_PAGE.HOME,
  DISPLAY_PAGE.STORE_HOME,
  DISPLAY_PAGE.SOLUTION_HOME,
  DISPLAY_PAGE.HOT_PRODUCTS_HOME,
  DISPLAY_PAGE.CATEGORY_AGGREGATE,
])

const STORE_AGGREGATE_BANNER_DISPLAY_PAGES = new Set([
  DISPLAY_PAGE.HOT_PRODUCTS_HOME,
  DISPLAY_PAGE.CATEGORY_AGGREGATE,
])

const COMPACT_COPY_BANNER_DISPLAY_PAGES = new Set([
  DISPLAY_PAGE.HOT_PRODUCTS_HOME,
  DISPLAY_PAGE.CATEGORY_AGGREGATE,
])

// 2. Props
const props = defineProps({
  displayPage: {
    type: Number,
    required: true,
  },
  showVisualOverlay: {
    type: Boolean,
    default: true,
  },
  categoryId: {
    type: [String, Number],
    default: null,
  },
  /**
   * @description 埋点配置：{ pvEvent, clickEvent, pageName, targetType? }
   * @type {Object|null}
   * @default null
   */
  tracking: {
    type: Object,
    default: null,
  },
})

// 3. Emits
const emit = defineEmits(['buy', 'empty'])

// 4. Refs/Reactive State
const { isMobile } = useMediaQuery()
const userStore = useUserStore()
const currentBannerIndex = ref(0)

const internalBanners = ref([])
const internalLoading = ref(true)

/** 与 SCSS `respond-to-max('lg')` / useMediaQuery.isMobile 对齐：max-width = lg - 1 */
const MOBILE_BANNER_MEDIA = `(max-width: ${parseInt(THEME_BREAKPOINTS.lg, 10) - 1}px)`

const effectiveBanners = computed(() => {
  const banners = internalBanners.value
  return banners.map((banner) => ({
    ...banner,
    theme: isMobile.value ? banner.mobileTheme : banner.pcTheme,
  }))
})

const effectiveLoading = computed(() => internalLoading.value)
const isStoreAggregateBanner = computed(() =>
  STORE_AGGREGATE_BANNER_DISPLAY_PAGES.has(props.displayPage)
)
const shouldUseCompactBannerCopy = computed(() =>
  COMPACT_COPY_BANNER_DISPLAY_PAGES.has(props.displayPage)
)
const isSolutionGuidelineBanner = computed(() => props.displayPage === DISPLAY_PAGE.SOLUTION_HOME)

/**
 * @description 上报 Banner 曝光
 * @param {Object} banner
 * @param {number} displayIndex - 从 1 开始
 * @returns {void}
 */
const trackBannerExposure = (banner, displayIndex) => {
  const tracking = props.tracking
  if (!tracking?.pvEvent || !banner) return
  const bannerId = String(banner.id ?? banner.bannerId ?? displayIndex)
  const exposureKey = `${tracking.pvEvent}_${bannerId}_${displayIndex}`
  if (hasExposedInPage(exposureKey)) return
  markExposedInPage(exposureKey)
  safeTrack({
    eventName: tracking.pvEvent,
    eventType: 'impression',
    targetType: tracking.targetType || 'homepage_banner',
    targetId: bannerId,
    targetName: banner.title || banner.mainTitle || '',
    targetIndex: displayIndex,
    pageName: tracking.pageName || '',
    properties: {
      banner_index: displayIndex,
      banner_id: bannerId,
      banner_name: banner.title || banner.mainTitle || '',
    },
  })
}

/**
 * @description 上报 Banner 点击
 * @param {Object} banner
 * @param {number} displayIndex
 * @returns {void}
 */
const trackBannerClick = (banner, displayIndex) => {
  const tracking = props.tracking
  if (!tracking?.clickEvent || !banner) return
  const bannerId = String(banner.id ?? banner.bannerId ?? displayIndex)
  const bannerName = banner.title || banner.mainTitle || ''
  const sourceType = tracking.targetType || 'homepage_banner'
  const basePayload = {
    eventType: 'custom_click',
    targetType: sourceType,
    targetId: bannerId,
    targetName: bannerName,
    targetIndex: displayIndex,
    pageName: tracking.pageName || '',
    sourceType,
    sourceId: bannerId,
    sourceName: bannerName,
    properties: {
      banner_index: displayIndex,
      banner_id: bannerId,
      banner_name: bannerName,
      source_type: sourceType,
      source_id: bannerId,
      source_name: bannerName,
    },
  }
  safeTrack({ eventName: tracking.clickEvent, ...basePayload })

  const productId = extractStoreProductIdFromUrl(banner?.link)
  if (productId) {
    rememberProductEntrySource(productId, {
      sourceType:
        sourceType.includes('mall') || tracking.pageName?.includes('商城')
          ? 'mall_banner'
          : sourceType.includes('scheme')
            ? 'scheme_banner'
            : 'homepage_banner',
      sourceId: bannerId,
      sourceName: bannerName,
      fromPage: sourceType,
    })
  }
}

/**
 * @description 上报当前顺位 Banner 曝光（需在视口内）
 * @param {number} index - 从 0 开始的轮播索引
 * @returns {Promise<void>}
 */
const reportBannerExposure = async (index) => {
  if (!props.tracking?.pvEvent) return
  const inView = await isSelectorInViewport('.home-banner', { threshold: 0.3 })
  if (!inView) return
  const banner = effectiveBanners.value[index]
  if (!banner) return
  trackBannerExposure(banner, index + 1)
}

/**
 * @description 判断 Banner 是否需要渲染 HTML 文案层（主题色仅在有文案时才有意义）
 * @param {Object} banner - Banner 数据或广告位原始 entry
 * @returns {boolean}
 */
const hasBannerContent = (banner) => {
  return [
    banner?.subtitle,
    banner?.title,
    banner?.desc,
    banner?.buttonText,
    banner?.subTitle,
    banner?.mainTitle,
    banner?.helpRemark,
    banner?.button,
  ].some((item) => String(item || '').trim())
}

/**
 * @description 仅预加载当前视口对应图片，避免 PC 拉 H5 / 移动端拉 PC
 * @param {Array} banners
 * @param {boolean} [mobile=isMobile.value]
 */
const resolveBannerImage = (banner, mobile = isMobile.value) =>
  mobile ? banner?.mobileImage || banner?.image || '' : banner?.image || banner?.mobileImage || ''

const preloadImages = (banners, mobile = isMobile.value) => {
  if (!banners || banners.length === 0) return

  banners.forEach((banner) => {
    const url = resolveBannerImage(banner, mobile)
    if (!url) return
    const img = new Image()
    img.src = url
  })
}

/**
 * @description 按端解析主题色采样参数
 * @param {boolean} forMobile
 * @returns {Object|null}
 */
const resolveBandThemeOptions = (forMobile) => {
  if (!BAND_IMAGE_THEME_DISPLAY_PAGES.has(props.displayPage)) return null
  if (forMobile) {
    return {
      centerBandWidth: 1280,
      leftSliceWidth: 640,
      sliceAlign: 'center',
      verticalSample: 'upper',
      brightnessThreshold: 165,
    }
  }
  return { centerBandWidth: 1280, leftSliceWidth: 640, sliceAlign: 'left' }
}

/**
 * @description 仅在有文案时按当前端取主题色；无文案直接 dark，避免无意义拉图采样
 * @param {Object} params
 * @param {string} params.pcImage
 * @param {string} params.mobileImage
 * @param {boolean} params.needsTheme
 * @returns {Promise<{ pcTheme: string, mobileTheme: string, pcThemeReady: boolean, mobileThemeReady: boolean }>}
 */
const resolveBannerThemes = async ({ pcImage, mobileImage, needsTheme }) => {
  if (import.meta.server) {
    return {
      pcTheme: 'dark',
      mobileTheme: 'dark',
      pcThemeReady: false,
      mobileThemeReady: false,
    }
  }

  if (!needsTheme) {
    return {
      pcTheme: 'dark',
      mobileTheme: 'dark',
      pcThemeReady: true,
      mobileThemeReady: true,
    }
  }

  const mobile = isMobile.value
  if (mobile) {
    const mobileTheme = await getImageTheme(
      mobileImage || pcImage,
      resolveBandThemeOptions(true) ?? {}
    )
    return {
      pcTheme: 'dark',
      mobileTheme,
      pcThemeReady: false,
      mobileThemeReady: true,
    }
  }

  const pcTheme = await getImageTheme(pcImage, resolveBandThemeOptions(false) ?? {})
  return {
    pcTheme,
    mobileTheme: 'dark',
    pcThemeReady: true,
    mobileThemeReady: false,
  }
}

// 5. Methods
const loadBanners = async (requestConfig = {}) => {
  const response = await websiteApi.getActiveEntries({}, requestConfig)
  const rawList = Array.isArray(response) ? response : (response?.data ?? response?.records ?? [])
  const activityEntries = rawList || []

  const normalizedCategoryId = String(props.categoryId ?? '').trim()
  const filteredEntries = activityEntries.filter((entry) => {
    if (Number(entry.displayPage) !== props.displayPage || Number(entry.adSlotType) !== 201) {
      return false
    }

    if (props.displayPage !== DISPLAY_PAGE.CATEGORY_AGGREGATE) {
      return true
    }

    if (!normalizedCategoryId) {
      return false
    }

    return String(entry.categoryId ?? '').trim() === normalizedCategoryId
  })

  const bannersDataPromises = filteredEntries.map(async (entry) => {
    const mobileImage = entry.imageUrl
      ? getOssOriginUrl(entry.imageUrl)
      : entry.pcImageUrl
        ? getOssOriginUrl(entry.pcImageUrl)
        : ''
    const pcImage = entry.pcImageUrl
      ? getOssOriginUrl(entry.pcImageUrl)
      : entry.imageUrl
        ? getOssOriginUrl(entry.imageUrl)
        : ''

    const subtitle = entry.subTitle || ''
    const title = entry.mainTitle || ''
    const desc = entry.helpRemark || ''
    const buttonText = entry.button || ''
    const needsTheme = hasBannerContent({
      subtitle,
      title,
      desc,
      buttonText,
    })

    const { pcTheme, mobileTheme, pcThemeReady, mobileThemeReady } = await resolveBannerThemes({
      pcImage,
      mobileImage,
      needsTheme,
    })

    return {
      subtitle,
      title,
      desc,
      image: pcImage,
      mobileImage,
      link: entry.entryLink || '',
      buttonText,
      pcTheme,
      mobileTheme,
      pcThemeReady,
      mobileThemeReady,
    }
  })

  const bannersData = await Promise.all(bannersDataPromises)
  if (import.meta.client) {
    preloadImages(bannersData)
  }
  return bannersData
}

const fetchBanners = async () => {
  internalLoading.value = true
  const retainedBanners = internalBanners.value
  try {
    const bannersData = await loadBanners()
    internalBanners.value = bannersData
    emit('empty', bannersData.length === 0)
  } catch (error) {
    console.error('获取轮播图数据失败:', error)
    BaseMessage.error('轮播图加载失败,请稍后重试')
    // Keep the rendered public snapshot when the identity-aware browser
    // refresh fails. A transient client error must not blank valid SSR data.
    internalBanners.value = retainedBanners
    emit('empty', retainedBanners.length === 0)
  } finally {
    internalLoading.value = false
  }
}

const bannerRequestKey = `base-home-banner:${props.displayPage}:${String(props.categoryId ?? '')}`
const publicContentSsrEnabled = usePublicContentSsrEnabled()
const { data: initialBanners } = await useAsyncData(
  bannerRequestKey,
  async () => {
    if (import.meta.client) return []
    if (userStore.isLoggedIn) return []

    try {
      return await loadBanners(createPublicContentSsrRequestConfig({ timeout: 3_000 }))
    } catch {
      return []
    }
  },
  usePublicContentAsyncDataOptions({ default: () => [] })
)

if (publicContentSsrEnabled.value && (import.meta.server || useNuxtApp().isHydrating)) {
  internalBanners.value = Array.isArray(initialBanners.value) ? initialBanners.value : []
  internalLoading.value = userStore.isLoggedIn && internalBanners.value.length === 0
  emit('empty', internalBanners.value.length === 0)
}

/**
 * @description 视口切换时补齐另一端主题色（仅有文案且尚未采样过的 Banner）
 * @param {boolean} mobile
 */
const ensureViewportTheme = async (mobile) => {
  const banners = internalBanners.value
  if (!banners.length) return

  const nextBanners = await Promise.all(
    banners.map(async (banner) => {
      if (!hasBannerContent(banner)) return banner

      if (mobile && !banner.mobileThemeReady) {
        const mobileTheme = await getImageTheme(
          banner.mobileImage || banner.image,
          resolveBandThemeOptions(true) ?? {}
        )
        return { ...banner, mobileTheme, mobileThemeReady: true }
      }

      if (!mobile && !banner.pcThemeReady) {
        const pcTheme = await getImageTheme(banner.image, resolveBandThemeOptions(false) ?? {})
        return { ...banner, pcTheme, pcThemeReady: true }
      }

      return banner
    })
  )

  internalBanners.value = nextBanners
}

const handleBannerChange = (index) => {
  currentBannerIndex.value = index
  reportBannerExposure(index)
}

const navigateBannerLink = (link) => {
  const target = String(link || '').trim()
  if (!target) return

  if (/^https?:\/\//i.test(target)) {
    navigateTo(target, { external: true })
    return
  }

  navigateTo(target)
}

const handleBannerAreaClick = () => {
  const currentBanner = effectiveBanners.value[currentBannerIndex.value]
  trackBannerClick(currentBanner, currentBannerIndex.value + 1)
  navigateBannerLink(currentBanner?.link)
}

const handleBuy = () => {
  const currentBanner = effectiveBanners.value[currentBannerIndex.value]
  trackBannerClick(currentBanner, currentBannerIndex.value + 1)
  navigateBannerLink(currentBanner?.link)
  emit('buy', currentBanner)
}

// 6. Lifecycle
onMounted(async () => {
  await fetchBanners()
  await nextTick()
  reportBannerExposure(currentBannerIndex.value)
})

watch(
  () => [props.displayPage, props.categoryId],
  () => {
    fetchBanners()
  }
)

watch(
  () => userStore.userInfo?.userType,
  (newUserType, oldUserType) => {
    if (oldUserType !== undefined && newUserType !== oldUserType) {
      fetchBanners()
    }
  }
)

watch(isMobile, async (mobile) => {
  preloadImages(internalBanners.value, mobile)
  await ensureViewportTheme(mobile)
})
</script>

<template>
  <section
    v-if="effectiveLoading || effectiveBanners.length > 0"
    class="home-banner"
    :class="{
      'home-banner--compact-copy': shouldUseCompactBannerCopy,
      'home-banner--aggregate': isStoreAggregateBanner,
      'home-banner--solution-guideline': isSolutionGuidelineBanner,
    }"
  >
    <BaseBanner
      v-if="effectiveBanners.length > 0"
      :interval="3"
      :carousel="true"
      default-theme="dark"
      :height="isSolutionGuidelineBanner ? '100%' : 'auto'"
      :show-nav="true"
      :show-indicators="true"
      @change="handleBannerChange"
    >
      <BaseBannerItem
        v-for="(banner, index) in effectiveBanners"
        :key="index"
        :index="index"
        :theme="banner.theme || 'dark'"
        :class="{ 'home-banner__slide--clickable': banner?.link }"
        @click="handleBannerAreaClick"
      >
        <picture class="banner-bg">
          <source
            v-if="banner.mobileImage"
            :media="MOBILE_BANNER_MEDIA"
            :srcset="banner.mobileImage"
          />
          <img
            :key="`${isMobile ? 'mobile' : 'desktop'}:${banner.mobileImage || banner.image || ''}`"
            class="banner-bg__img"
            :src="resolveBannerImage(banner)"
            alt=""
            draggable="false"
          />
        </picture>
        <div v-if="showVisualOverlay && !isMobile" class="banner-overlay"></div>
        <div v-if="showVisualOverlay && !isMobile" class="banner-grid"></div>
        <div
          class="banner-content base-container"
          :class="{
            'banner-content--clickable': banner?.link,
          }"
        >
          <div
            v-if="hasBannerContent(banner)"
            class="banner-info"
            :class="`banner-info--theme-${banner.theme || 'dark'}`"
          >
            <div v-if="banner?.subtitle" class="overflow-hidden">
              <p class="banner-content__subtitle">{{ banner?.subtitle }}</p>
            </div>
            <div v-if="banner?.title" class="overflow-hidden">
              <h1 class="banner-content__title">{{ banner?.title }}</h1>
            </div>
            <div v-if="banner?.desc" class="overflow-hidden">
              <p class="banner-content__desc">{{ banner?.desc }}</p>
            </div>
            <div v-if="banner?.buttonText" class="banner-content__actions" @click.stop>
              <a-button
                type="primary"
                :size="isMobile ? 'small' : 'medium'"
                class="buy-btn"
                @click="handleBuy"
              >
                {{ banner?.buttonText }}
              </a-button>
            </div>
          </div>
        </div>
      </BaseBannerItem>
    </BaseBanner>
    <div v-else-if="effectiveLoading" class="home-banner__placeholder" aria-hidden="true"></div>
  </section>
</template>

<style lang="scss" scoped>
.home-banner {
  --home-banner-aspect-ratio: 2560 / 1227;
  width: 100%;
  position: relative;

  &__placeholder {
    width: 100%;
    aspect-ratio: var(--home-banner-aspect-ratio);
    background: $bg-color;
  }

  @include respond-to-max('lg') {
    --home-banner-aspect-ratio: 1200 / 2174;
  }

  &--aggregate {
    --home-banner-aspect-ratio: 1920 / 600;

    @include respond-to-max('lg') {
      --home-banner-aspect-ratio: 375 / 500;
    }
  }

  &__slide--clickable {
    cursor: pointer;
  }

  :deep(.base-banner) {
    aspect-ratio: var(--home-banner-aspect-ratio);
  }

  .banner-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    pointer-events: none;
    z-index: 0;
  }

  .banner-bg__img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    border: 0;

    @include respond-to-max('lg') {
      object-position: center center;
    }
  }

  .banner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba($bg-dark, 0.8) 0%,
      rgba($bg-dark, 0.2) 60%,
      transparent 100%
    );
    z-index: 1;
  }

  .banner-grid {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: radial-gradient(circle, rgba($bg-white, 0.05) 1px, transparent 1px);
    background-size: 30px 30px;
    z-index: 2;
    pointer-events: none;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 150px;
      background: linear-gradient(to top, $bg-dark 0%, transparent 100%);
    }
  }

  .banner-content {
    position: relative;
    z-index: 3;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    width: 100%;

    &--clickable {
      cursor: pointer;
    }

    @include respond-to-max('lg') {
      justify-content: flex-start;
    }
  }

  .banner-info {
    width: 100%;
    text-align: left;
    margin-right: auto;
    margin-left: 0;
    padding-top: 0;
    padding-bottom: 0;
    margin-bottom: 0;

    &--theme-dark {
      color: $bg-white;
    }

    &--theme-light {
      color: $text-title-color;
    }

    @include respond-to-max('lg') {
      text-align: center;
      margin-right: auto;
      margin-left: auto;
      padding-top: 44px;
      padding-bottom: 100px;
    }

    .overflow-hidden {
      overflow: hidden;
    }
  }

  .banner-content__subtitle {
    color: $primary-color;
    display: inline-block;
    /* text-transform: uppercase; */
    font-size: clamp(20px, 1.6667vw, 32px);
    line-height: clamp(30px, 2.5vw, 48px);

    @include respond-to-max('lg') {
      text-align: center;
      font-size: 14px;
      line-height: 22px;
    }
  }

  .banner-content__title {
    margin-top: clamp(12px, 1.0417vw, 20px);
    font-size: clamp(64px, 5.625vw, 108px);
    font-weight: $font-weight-bold;
    line-height: clamp(78px, 6.875vw, 132px);
    white-space: pre-line;

    @include respond-to-max('lg') {
      font-size: 20px;
      line-height: 28px;
      text-align: center;
      margin-top: 8px;
    }
  }

  .banner-content__desc {
    margin-top: clamp(12px, 1.0417vw, 20px);
    font-size: clamp(20px, 1.6667vw, 32px);
    line-height: clamp(30px, 2.5vw, 48px);

    @include respond-to-max('lg') {
      text-align: center;
      font-size: 14px;
      line-height: 22px;
      margin-top: 8px;
    }
  }

  .banner-content__actions {
    margin-top: clamp(24px, 2.0833vw, 40px);

    @include respond-to-max('lg') {
      display: flex;
      margin-top: 12px;
      justify-content: center;
    }
  }

  .buy-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: clamp(88px, 5.8333vw, 112px);
    height: clamp(36px, 2.5vw, 48px);
    padding: 0 clamp(18px, 1.25vw, 24px);
    color: $bg-white;
    background-color: $primary-color;
    background-image: $banner-button-gradient;
    border-color: transparent;
    border-radius: clamp(4px, 0.3125vw, 6px);
    font-size: clamp(14px, 1.25vw, 16px);
    font-weight: $font-weight-medium;
    line-height: 1;
    box-sizing: border-box;

    &:hover {
      color: $bg-white;
      background-color: $primary-color;
      background-image: $banner-button-gradient;
      border-color: transparent;
    }

    &:active {
      color: $bg-white;
      background-color: $primary-color;
      background-image: $banner-button-gradient;
      border-color: transparent;
    }
  }

  &--compact-copy {
    .banner-content__subtitle {
      font-size: clamp(12px, 1.25vw, 24px);
      line-height: clamp(22px, 1.6667vw, 32px);
    }

    .banner-content__title {
      font-size: clamp(32px, 3.3333vw, 64px);
      line-height: clamp(40px, 3.75vw, 72px);
    }

    .banner-content__desc {
      font-size: clamp(14px, 1.4583vw, 28px);
      line-height: clamp(22px, 1.875vw, 36px);
    }
  }

  @include respond-to-max('lg') {
    .banner-content {
      padding: 0;
    }

    .banner-info {
      width: 100%;
      padding-top: 24px;
      padding-bottom: 0;
      color: $bg-white;

      .overflow-hidden {
        width: 100%;
      }

      .banner-content__subtitle {
        display: block;
        width: 100%;
        margin: 0;
        color: #ff7d00;
        font-size: 14px;
        font-weight: $font-weight-regular;
        line-height: 22px;
        white-space: nowrap;
      }

      .banner-content__title {
        width: 100%;
        max-width: none;
        margin: 8px 0 0;
        color: $bg-white;
        font-size: 36px;
        font-weight: $font-weight-bold;
        line-height: 48px;
        text-align: center;
        white-space: pre-line;
      }

      .banner-content__desc {
        width: 100%;
        margin: 4px 0 0;
        color: $bg-white;
        font-size: 14px;
        line-height: 22px;
        text-align: center;
        white-space: nowrap;
      }

      .banner-content__actions {
        display: flex;
        justify-content: center;
        margin-top: 12px;
      }

      .buy-btn {
        width: auto;
        min-width: 88px;
        height: 32px;
        padding: 0 10px;
        border-radius: $radius-6;
        font-size: 14px;
        font-weight: $font-weight-medium;
        line-height: 22px;
      }
    }

    &--compact-copy {
      .banner-info {
        .banner-content__subtitle {
          font-size: 12px;
          line-height: 22px;
        }

        .banner-content__title {
          font-size: 32px;
          line-height: 40px;
        }
      }

      .banner-info .banner-content__desc {
        font-size: 14px;
        line-height: 22px;
      }
    }
  }

  @include respond-to('lg') {
    @media (max-width: 1620px) {
      :deep(.base-banner__nav) {
        padding: 0 $spacing-2xl;
      }

      .banner-info {
        padding-left: $spacing-xl;
      }
    }
  }

  &--solution-guideline {
    @include website-guideline-banner-shell;

    :deep(.base-banner) {
      height: 100%;
      aspect-ratio: auto;
    }

    .banner-content {
      @include website-guideline-banner-content;
    }

    .banner-info {
      @include website-guideline-banner-copy;
    }

    .banner-info .banner-content__title {
      @include website-guideline-banner-title;
    }

    .banner-info .banner-content__desc {
      @include website-guideline-banner-description;
    }

    .banner-info .banner-content__actions {
      @include website-guideline-banner-actions;
    }

    .banner-info .buy-btn {
      @include website-guideline-banner-button;
    }
  }
}
</style>
